import type { ReactNode } from 'react';

import type {
  ContentfulNavQuickLinkFragment,
  ContentfulNavQuickLinksFragment,
} from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { containsPattern } from '@/lib/util';

import { Typography } from '../../../../Typography';
import { useHandleQuickLink } from './helpers';

import { ReactComponent as EasyContactIcon } from '@/assets/icons/concierge.svg';
import { ReactComponent as GiftOfferedIcon } from '@/assets/icons/gift.svg';
import { ReactComponent as FreeReturnIcon } from '@/assets/icons/return.svg';
import { ReactComponent as FreeShippingIcon } from '@/assets/icons/shipping.svg';

import * as S from './NavQuickLinkItem.styles';

const ICONS_MAPPING: Record<string, ReactNode> = {
  shipping: <FreeShippingIcon data-test-id='NavQuickLinkItemShippingIcon' />,
  gift: <GiftOfferedIcon data-test-id='NavQuickLinkItemGiftIcon' />,
  'free-return': (
    <FreeReturnIcon data-test-id='NavQuickLinkItemFreeReturnIcon' />
  ),
  'easy-contact': (
    <EasyContactIcon data-test-id='NavQuickLinkItemEasyContactIcon' />
  ),
};

export interface NavQuickLinkProps extends ContentfulNavQuickLinkFragment {
  className?: string;
  type?: ContentfulNavQuickLinksFragment['type'];
}

export const NavQuickLinkItem = ({
  backgroundColor,
  className,
  icon,
  media,
  type,
  url,
  urlTarget,
  urlText,
  urlTrigger,
}: NavQuickLinkProps) => {
  const { asPath } = useRouter();
  const { handleQuickLink } = useHandleQuickLink(
    url ?? '/',
    urlTarget ?? '_self',
    urlTrigger,
  );
  if (type === 'Text') {
    const isContains = containsPattern(asPath, url);
    const isSelected = isContains || url === asPath;

    const isSvgImage = media?.contentType?.includes('svg') ?? false;
    return (
      <S.LinkButtonStyled
        $isSvgImage={isSvgImage}
        $selected={isSelected}
        className={className}
        onClick={handleQuickLink}
      >
        <S.LinkButtonWrapper>
          {media && (
            <S.LinkButtonIcon>
              <S.Img
                alt={media.title ?? ''}
                aria-hidden
                className={className}
                data-testid='servicesImage'
                height={24}
                sizes='50vw'
                src={media.url ?? ''}
                width={24}
              />
            </S.LinkButtonIcon>
          )}
          <Typography>{urlText}</Typography>
        </S.LinkButtonWrapper>
      </S.LinkButtonStyled>
    );
  }
  const hasMatchingIcon = icon && icon in ICONS_MAPPING;

  return (
    <S.Wrapper
      className={className}
      data-testid='NavQuickLinkItem'
      onClick={handleQuickLink}
    >
      {icon && hasMatchingIcon && ICONS_MAPPING[icon]}
      {media && (
        <S.ImgBox $backgroundColor={backgroundColor as string}>
          <S.Img
            alt={media.title ?? ''}
            aria-hidden
            className={className}
            data-testid='servicesImage'
            fill
            height={0}
            loaderHeight={350}
            loading='eager'
            sizes='50vw'
            src={media.url ?? ''}
            width={0}
          />
        </S.ImgBox>
      )}
      <S.Label data-test-id='NavQuickLinkItemLabel'>{urlText}</S.Label>
    </S.Wrapper>
  );
};
